import { Modal } from '@components/molecules/Modal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { FC, useEffect, useState, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import GoogleAddressInput from '@components/atoms/GoogleAddressInput';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { Project } from '@models/project.model';
import { useClients } from '@hooks/api';
import { useEmployees } from '@hooks/api';
import { useEditProject } from '@hooks/api/projects';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { formatDateForRequest } from '@utils/formatDate';
import { toast } from 'react-toastify';
import { invoiceService } from '@services/invoice';
import { useTranslation } from '@hooks/useTranslation';
import {
  PROJECT_STATE_DEFINITIONS,
  buildProjectPayload,
  buildProjectValidationSchema,
  mapBackendStateToFrontend,
} from '../../utils/projectForm';

interface CreateEditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Sprint 31, story-160: this component is edit-only from here on — project *creation* moved
   * to `ProjectWizardPage` (`/projects/new`). `initialData` is now required rather than optional.
   */
  initialData: Project;
  onSuccess?: (project: Project) => void;
}

export const CreateEditProjectModal: FC<CreateEditProjectModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSuccess,
}) => {
  const t = useTranslation('projects');
  const tCommon = useTranslation('common');

  // Sprint 31, story-156. Translated view of PROJECT_STATE_DEFINITIONS — same shape (`id`/`name`/
  // `backendValue`) every downstream call site below already expects.
  const states = useMemo(
    () =>
      PROJECT_STATE_DEFINITIONS.map((s) => ({
        id: s.id,
        name: t[s.nameKey],
        backendValue: s.backendValue,
      })),
    [t],
  );

  const validationSchema = useMemo(() => buildProjectValidationSchema(t), [t]);

  const { data: clients } = useClients();
  const { data: projectManagersData } = useEmployees({
    role: 'PROJECT_MANAGER',
    limit: 100,
  });
  const { data: superintendentsData } = useEmployees({
    role: 'SUPERINTENDENT',
    limit: 100,
  });
  const { data: reviewersData } = useEmployees({
    role: 'REVIEWER',
    limit: 100,
  });
  const editProjectMutation = useEditProject();

  // State
  const [siteImageFiles, setSiteImageFiles] = useState<File[]>([]);
  // Pre-COMPLETE invoice-pending warning (mirrors the per-task warning in CreateEditTaskModal)
  const [showInvoicePendingModal, setShowInvoicePendingModal] = useState(false);
  const [invoicePendingSummary, setInvoicePendingSummary] = useState<{
    taskCount: number;
    materialCount: number;
    equipmentCount: number;
    sampleNames: string[];
  } | null>(null);
  const invoicePendingConfirmedRef = useRef(false);

  // Edit-only from here on: initialData is always present, so these read straight from it
  // rather than falling back to a "pre-select the current user" default (that default only
  // ever applied on create, which now happens on the wizard's Team & client step instead).
  const defaultSuperintendentIds = useMemo(() => {
    if (initialData.superintendentIds) return initialData.superintendentIds;
    if (initialData.superintendents) {
      return initialData.superintendents.map((s) => s.employeeId).filter((id): id is string => !!id);
    }
    return [];
  }, [initialData.superintendentIds, initialData.superintendents]);

  const defaultReviewerEmployeeIds = useMemo(() => {
    if (initialData.reviewerEmployeeIds) return initialData.reviewerEmployeeIds;
    if (initialData.reviewers) {
      return initialData.reviewers.map((r) => r.employeeId).filter((id): id is string => !!id);
    }
    return [];
  }, [initialData.reviewerEmployeeIds, initialData.reviewers]);

  const formik = useFormik({
    initialValues: {
      name: initialData.name || '',
      address: initialData.address || '',
      description: initialData.description || '',
      client: initialData.clientId || '',
      budget: initialData.budget || 0,
      totalManHours: initialData.totalManHours ?? ('' as number | string),
      startDate: initialData.startDate ? (formatDateForRequest(initialData.startDate, true) as string) || '' : '',
      endDate: initialData.endDate ? (formatDateForRequest(initialData.endDate, true) as string) || '' : '',
      lat: initialData.lat?.toString() || '',
      long: initialData.long?.toString() || '',
      state: initialData.state ? mapBackendStateToFrontend(initialData.state) : 'ongoing',
      projectManagerId: initialData.projectManagerId || '',
      superintendentIds: defaultSuperintendentIds,
      reviewerEmployeeIds: defaultReviewerEmployeeIds,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // Invoice-pending gate: when marking an existing project COMPLETED (and it was not
        // already completed), warn if any of its tasks still have un-invoiced
        // materials/equipment. On confirm the user re-submits with the ref set; the backend
        // creates the INVOICE_PENDING activities on the transition. Best-effort: a failed
        // summary lookup never blocks the save.
        const priorState = initialData.state ? mapBackendStateToFrontend(initialData.state) : '';
        const isCompletingProject = values.state === 'completed' && priorState !== 'completed';
        if (isCompletingProject && !invoicePendingConfirmedRef.current) {
          try {
            const summary = await invoiceService.getProjectInvoicePendingSummary(initialData.projectId!);
            if (summary.materialCount + summary.equipmentCount > 0) {
              setInvoicePendingSummary(summary);
              setShowInvoicePendingModal(true);
              return;
            }
          } catch (err) {
            console.warn(
              'Project invoice pending summary check failed; continuing without warning',
              err,
            );
          }
        }
        invoicePendingConfirmedRef.current = false;

        const payload = buildProjectPayload(values, states);
        const selectedFile = siteImageFiles.length > 0 ? siteImageFiles[0] : null;
        const updatePayload = selectedFile
          ? { projectId: initialData.projectId!, ...payload, siteImage: selectedFile }
          : { projectId: initialData.projectId!, ...payload };

        const result = await editProjectMutation.mutateAsync(updatePayload as any);

        // Close modal first
        onClose();

        // Small delay to allow queries to start refetching
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Call onSuccess callback with the updated project
        // This ensures the project list is updated before navigation
        if (onSuccess && result) {
          onSuccess(result);
        }
      } catch (error: any) {
        console.error('Error submitting form:', error);
        toast.error(error?.message || t.submitFailedFallback);
        // Don't close modal - let user retry
      }
    },
  });

  const clientOptions: SelectOption[] =
    clients
      ?.filter((client) => client.clientId)
      .map((client) => ({
        label: client.name,
        value: client.clientId!,
      })) || [];

  const projectManagerOptions: SelectOption[] =
    projectManagersData?.employees
      ?.filter((employee) => employee.employeeId)
      .map((employee) => ({
        label:
          `${employee.user?.firstName || ''} ${employee.user?.lastName || ''}`.trim() ||
          t.unknownEmployeeFallback,
        value: employee.employeeId!,
      })) || [];

  const superintendentOptions: SelectOption[] =
    superintendentsData?.employees
      ?.filter((employee) => employee.employeeId)
      .map((employee) => ({
        label:
          `${employee.user?.firstName || ''} ${employee.user?.lastName || ''}`.trim() ||
          t.unknownEmployeeFallback,
        value: employee.employeeId!,
      })) || [];

  const reviewerOptions: SelectOption[] =
    reviewersData?.employees
      ?.filter((employee) => employee.employeeId)
      .map((employee) => ({
        label:
          `${employee.user?.firstName || ''} ${employee.user?.lastName || ''}`.trim() ||
          employee.user?.email ||
          t.unknownReviewerFallback,
        value: employee.employeeId!,
      })) || [];

  const {
    values,
    handleChange,
    submitForm,
    setFieldValue,
    errors,
    touched,
    resetForm,
  } = formik;

  const isLoading = editProjectMutation.isPending;

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setSiteImageFiles([]);
      setShowInvoicePendingModal(false);
      setInvoicePendingSummary(null);
      invoicePendingConfirmedRef.current = false;
    }
  }, [isOpen, resetForm]);

  const handleConfirmInvoicePending = () => {
    invoicePendingConfirmedRef.current = true;
    setShowInvoicePendingModal(false);
    submitForm();
  };

  const handleCancelInvoicePending = () => {
    setShowInvoicePendingModal(false);
    setInvoicePendingSummary(null);
  };

  const invoicePendingDescription = (() => {
    if (!invoicePendingSummary) return '';
    const { taskCount, materialCount, equipmentCount, sampleNames } = invoicePendingSummary;
    const parts: string[] = [];
    // Sprint 31, story-156/story-158 overlap: `count === 1` (not `count > 1`), per house style
    // §13.2 — zero is plural in Spanish, and these branches are only reached with count > 0
    // anyway, but the comparison itself must still be the correct one.
    if (materialCount > 0) {
      parts.push(
        (materialCount === 1 ? t.invoicePendingMaterialOne : t.invoicePendingMaterialOther).replace(
          '{n}',
          String(materialCount),
        ),
      );
    }
    if (equipmentCount > 0) {
      parts.push(
        (equipmentCount === 1
          ? t.invoicePendingEquipmentOne
          : t.invoicePendingEquipmentOther
        ).replace('{n}', String(equipmentCount)),
      );
    }
    const items = parts.join(t.invoicePendingJoiner);
    const taskLabel = (taskCount === 1 ? t.invoicePendingTaskOne : t.invoicePendingTaskOther).replace(
      '{n}',
      String(taskCount),
    );
    const sample =
      sampleNames.length > 0
        ? t.invoicePendingSampleSuffix.replace('{names}', sampleNames.join(', '))
        : '';
    return t.invoicePendingDescription
      .replace('{items}', items)
      .replace('{taskLabel}', taskLabel)
      .replace('{sample}', sample);
  })();

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      handleConfirm={submitForm}
      title={t.editProjectModalTitle}
      confirmText={t.updateProjectButton}>
      <form className="flex flex-col gap-6">
        <FileDropZone
          files={siteImageFiles}
          onFilesChange={(files) => setSiteImageFiles(files.slice(-1))}
          multiple={false}
          maxFiles={1}
          acceptedTypes="images"
          avatar
          existingImageUrl={initialData?.siteImage}
          title={t.siteImageDropTitle}
          subtitle={t.siteImageDropSubtitle}
        />
        <div className="flex flex-col gap-4">
          <FormGroup
            error={errors.name || undefined}
            htmlFor="name"
            label={t.name}>
            <Input
              name="name"
              value={values.name}
              onChange={handleChange}
              placeholder={t.namePlaceholder}
              error={!!(touched.name && errors.name)}
              data-testid="project-form-name-input"
            />
          </FormGroup>
          <FormGroup
            error={errors.state || undefined}
            htmlFor="state"
            label={t.stateLabel}>
            <ReactSelect
              name="state"
              value={
                states
                  .map((s) => ({ label: s.name, value: s.id }))
                  .find((opt) => opt.value === values.state) as
                  | SelectOption
                  | undefined
              }
              onChange={(selectedOption) => {
                const option = selectedOption as SelectOption | null;
                setFieldValue('state', option?.value || 'ongoing');
              }}
              options={states.map((state) => ({
                label: state.name,
                value: state.id,
              }))}
              placeholder={t.statePlaceholder}
              isSearchable
            />
          </FormGroup>
          <FormGroup htmlFor="description" label={t.descriptionLabel}>
            <TextareaInput
              name="description"
              rows={4}
              value={values.description}
              onChange={handleChange}
              placeholder={t.descriptionPlaceholder}
            />
            {/* Sprint 25, story-128 (D8). project.description IS the client-facing
                description — shown unconditionally on the shared create/edit modal, simpler and
                safer than conditionally hiding it based on whether a client is currently
                selected in the same form state. */}
            <p className="text-xs text-gray-500 mt-1">
              {t.descriptionClientVisibleNote}
            </p>
          </FormGroup>
        </div>
        <div className="flex gap-4">
          <FormGroup
            error={errors.startDate || undefined}
            htmlFor="startDate"
            label={t.startDateLabel}>
            <Input
              type="date"
              name="startDate"
              value={values.startDate || ''}
              onChange={handleChange}
              placeholder={t.startDatePlaceholder}
              error={!!(touched.startDate && errors.startDate)}
            />
          </FormGroup>
          <FormGroup
            error={errors.endDate || undefined}
            htmlFor="endDate"
            label={t.endDateLabel}>
            <Input
              type="date"
              name="endDate"
              value={values.endDate || ''}
              onChange={handleChange}
              placeholder={t.endDatePlaceholder}
              error={!!(touched.endDate && errors.endDate)}
            />
          </FormGroup>
        </div>
        <FormGroup
          error={errors.address || undefined}
          htmlFor="address"
          label={t.address}>
          <GoogleAddressInput
            name="address"
            value={values.address}
            onChange={(value) => {
              setFieldValue('address', value);
            }}
            onChangeCoor={(lat, long) => {
              setFieldValue('lat', lat?.toString() || '');
              setFieldValue('long', long?.toString() || '');
            }}
            placeholder={t.addressPlaceholder}
            error={!!(touched.address && errors.address)}
          />
        </FormGroup>
        <div className="flex gap-4">
          <FormGroup htmlFor="projectManagerId" label={t.projectManagerLabel}>
            <ReactSelect
              name="projectManagerId"
              onChange={(selectedOption) => {
                const option = selectedOption as SelectOption | null;
                setFieldValue('projectManagerId', option?.value || '');
              }}
              value={
                projectManagerOptions.find(
                  (manager) => manager.value === values.projectManagerId,
                ) as SelectOption | undefined
              }
              options={projectManagerOptions}
              placeholder={t.projectManagerPlaceholder}
              isSearchable
              isClearable
            />
          </FormGroup>
          <FormGroup htmlFor="superintendentIds" label={t.superintendentsLabel}>
            <ReactSelect
              isMulti
              name="superintendentIds"
              onChange={(selectedOptions) => {
                const options = Array.isArray(selectedOptions)
                  ? selectedOptions.map((opt: any) => opt.value)
                  : [];
                setFieldValue('superintendentIds', options);
              }}
              value={superintendentOptions.filter((superintendent) =>
                values.superintendentIds.includes(
                  superintendent.value as string,
                ),
              )}
              options={superintendentOptions}
              placeholder={t.superintendentsPlaceholder}
              isSearchable
            />
          </FormGroup>
        </div>
        <div className="flex gap-4">
          <FormGroup htmlFor="reviewerEmployeeIds" label={t.reviewersLabel}>
            <ReactSelect
              isMulti
              name="reviewerEmployeeIds"
              onChange={(selectedOptions) => {
                const options = Array.isArray(selectedOptions)
                  ? selectedOptions.map((opt: any) => opt.value)
                  : [];
                setFieldValue('reviewerEmployeeIds', options);
              }}
              value={reviewerOptions.filter((reviewer) =>
                values.reviewerEmployeeIds.includes(reviewer.value as string),
              )}
              options={reviewerOptions}
              placeholder={t.reviewersPlaceholder}
              isSearchable
            />
          </FormGroup>
        </div>
        <div className="flex gap-4">
          <FormGroup htmlFor="client" label={t.clientLabel}>
            <ReactSelect
              name="client"
              onChange={(selectedOption) => {
                const option = selectedOption as SelectOption | null;
                setFieldValue('client', option?.value || '');
              }}
              value={
                clientOptions.find(
                  (client) => client.value === values.client,
                ) as SelectOption | undefined
              }
              options={clientOptions}
              placeholder={t.clientPlaceholder}
              isSearchable
            />
          </FormGroup>
        </div>
        <div className="flex gap-4">
          <FormGroup htmlFor="budget" label={t.budgetLabel}>
            <InputCurrency
              currency="USD"
              name="budget"
              value={values.budget}
              onValueChange={(value) => {
                setFieldValue('budget', value || 0);
              }}
              placeholder={t.budgetPlaceholder}
              error={!!(touched.budget && errors.budget)}
            />
          </FormGroup>
          <FormGroup
            error={errors.totalManHours || undefined}
            htmlFor="totalManHours"
            label={t.totalManHoursLabel}>
            <Input
              type="number"
              min={0}
              name="totalManHours"
              value={values.totalManHours}
              onChange={handleChange}
              placeholder={t.totalManHoursPlaceholder}
              error={!!(touched.totalManHours && errors.totalManHours)}
            />
          </FormGroup>
        </div>
      </form>
    </Modal>

    <ConfirmationModal
      variant="warning"
      closeOnEscape={false}
      closeOnOverlayClick={false}
      title={t.pendingInvoiceItemsTitle}
      isOpen={showInvoicePendingModal}
      confirmText={t.completeAnywayButton}
      cancelText={tCommon.cancel}
      onConfirm={handleConfirmInvoicePending}
      onClose={handleCancelInvoicePending}
      description={invoicePendingDescription}
    />
    </>
  );
};
